import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowRight, Sparkles, Search as SearchIcon, Check, X as XIcon, Loader2, Play, Command, AlignLeft } from 'lucide-react';
import { toast } from 'sonner';
import { getDeviceId, stashPendingClaim } from '../lib/tryItStorage';
import { track } from '../lib/analytics';
import { useAuth } from '../contexts/AuthContext';
import EmailAuthSheet from '../components/EmailAuthSheet';

const API = '/api';

// Static starter tiles — one-way binding to backend `starter_bookmarks.json`.
// A dead ID would show a broken card; audited Aug 23 2026 (see /app/backend/data/starter_bookmarks.json).
const STARTERS = [
    { id: 'starter-gordon-eggs', yt: 'VhJFyyukAzA', title: 'Gordon Ramsay: Perfect Scrambled Eggs', kicker: 'RECIPE', duration: '3m' },
    { id: 'starter-jobs-stanford', yt: 'UF8uR6Z6KLc', title: "Steve Jobs' Stanford commencement", kicker: 'TALK', duration: '15m' },
    { id: 'starter-nyc-things', yt: 'SlAKvbeG27Q', title: 'Top 10 things to do in New York City', kicker: 'TRAVEL', duration: '12m' },
    { id: 'starter-ted-stress-brain', yt: 'WuyPuH9ojCE', title: 'How stress affects your brain', kicker: 'SCIENCE', duration: '5m' },
    { id: 'starter-sinek-why', yt: 'TDi4hzRIrgc', title: 'Simon Sinek - Start With Why', kicker: 'IDEAS', duration: '18m' },
];

// ── screens ──────────────────────────────────────────────────
const S_LANDING = 'landing';
const S_PROCESSING = 'processing';
const S_RESULT = 'result';
const S_PROCESSING_ERROR = 'processing_error';


// Small palette helpers.
const EMERALD = '#10B981';
const INK = '#111827';
const SECONDARY = '#5F6D66';
const CANVAS = '#FDFCF8';

export default function TryItFlow() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, user, loading: authLoading } = useAuth();
    const [screen, setScreen] = useState(S_LANDING);
    const [url, setUrl] = useState('');
    const [bookmark, setBookmark] = useState(null);        // filled after processing
    const [error, setError] = useState('');
    const [starting, setStarting] = useState(false);
    const [processingCompleted, setProcessingCompleted] = useState(false);
    const [showSignupWall, setShowSignupWall] = useState(false);
    const [showEmailSheet, setShowEmailSheet] = useState(false);
    const [emailMode, setEmailMode] = useState('register');
    // ?whoami=1 payload — inline banner instead of a toast because the
    // `<Toaster />` isn't mounted at the /try route.
    const [whoami, setWhoami] = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    // Auth guard: an already-authenticated visitor (e.g. after
    // completing Google OAuth from inside the sign-up wall, or a
    // returning user who lands on /try via a marketing link) should
    // skip the anonymous landing entirely and go straight to their
    // real library. Without this, the previous release trapped users
    // on the anonymous /try page even after their session cookie was
    // set — they had to close and reopen the TWA to escape. (Bug
    // reported via video Aug 23 2026.)
    useEffect(() => {
        if (authLoading) return;
        if (user) {
            navigate('/dashboard', { replace: true });
        }
    }, [user, authLoading, navigate]);

    // Consume ?signup=1 / ?login=1 / ?google=1 on mount. These are set
    // by BookmarkDetail's "Keep this forever" bar and by the sign-up
    // wall's Google button, respectively. Strip the params afterward
    // so browser back doesn't re-trigger.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const wantSignup = params.get('signup') === '1';
        const wantLogin = params.get('login') === '1';
        const wantGoogle = params.get('google') === '1';
        const wantWhoAmI = params.get('whoami') === '1';

        // Owner-testing helper: `?whoami=1` reveals this browser's
        // generated try-it device_id via a persistent toast + copy
        // button. Paste it into the TRY_IT_BYPASS_DEVICE_IDS env var
        // to opt out of the per-device / per-IP anonymous save caps.
        // No auth required — the device_id isn't a secret, it's just
        // a random UUID minted client-side; showing it here saves a
        // trip through chrome://inspect. (Added Aug 27 2026.)
        if (wantWhoAmI) {
            params.delete('whoami');
            const q = params.toString();
            try { window.history.replaceState({}, '', location.pathname + (q ? '?' + q : '')); } catch (_) { /* noop */ }
            setWhoami(getDeviceId());
            return; // don't also fire signup/login/google on this visit
        }

        if (!wantSignup && !wantLogin && !wantGoogle) return;
        params.delete('signup'); params.delete('login'); params.delete('google');
        const q = params.toString();
        try { window.history.replaceState({}, '', location.pathname + (q ? '?' + q : '')); } catch (_) { /* noop */ }
        if (wantGoogle) {
            login();
        } else if (wantSignup) {
            setEmailMode('register'); setShowEmailSheet(true);
        } else if (wantLogin) {
            setEmailMode('login'); setShowEmailSheet(true);
        }
    }, [location.search, location.pathname, login]);

    // ── URL submit / starter tap → kick off processing ──
    // Flow order (Fix A, Aug 2026): flip to the processing screen
    // FIRST, then start the network. Users used to stare at the
    // static landing for the full ingest duration (up to 15 s for
    // videos) with only a tiny button spinner as feedback — now the
    // paced checklist mounts within one frame and the title /
    // thumbnail merge in as they resolve.
    const startProcessing = useCallback(async (targetUrl, opts = {}) => {
        setError('');
        if (starting) return;
        const trimmed = (targetUrl || '').trim();
        if (!trimmed && !opts.starter) {
            const pick = STARTERS[Math.floor(Math.random() * STARTERS.length)];
            return startProcessing('', { starter: pick.id, starterTitle: pick.title });
        }
        setStarting(true);
        setProcessingCompleted(false);
        // 1) Instant screen swap with a placeholder bookmark.
        setBookmark({
            bookmark_id: null,
            url: trimmed || 'Starter clip',
            title: opts.starterTitle,
            status: 'pending',
            starter: !!opts.starter,
        });
        const tapAt = performance.now();
        setScreen(S_PROCESSING);
        // Fix A analytics: measure the tap → first-paint gap. The
        // rAF captures the actual paint after state commits (React
        // 18 batches setScreen into the next microtask; rAF fires on
        // the following frame).
        requestAnimationFrame(() => {
            try {
                track('processing_screen_shown', {
                    latency_ms: Math.round(performance.now() - tapAt),
                    source: opts.starter ? 'starter' : 'url',
                });
            } catch (_) { /* analytics is best-effort */ }
        });
        // 2) Fire the ingest / starter-clone in parallel with the UI.
        try {
            const device_id = getDeviceId();
            const endpoint = opts.starter
                ? `${API}/bookmarks/starter/${opts.starter}/try?device_id=${encodeURIComponent(device_id)}`
                : `${API}/try/bookmark`;
            const init = opts.starter
                ? { method: 'POST' }
                : {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ url: trimmed, device_id }),
                };
            const res = await fetch(endpoint, init);
            if (res.status === 429) {
                // Rate limited → open the sign-up sheet with the
                // "Keep it forever?" copy (per Fix A step 2).
                try { track('anon_ingest_failed_ui', { reason: '429' }); } catch (_) {}
                setStarting(false);
                setEmailMode('register');
                setShowEmailSheet(true);
                setScreen(S_LANDING);
                return;
            }
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                try { track('anon_ingest_failed_ui', { reason: 'invalid', status: res.status }); } catch (_) {}
                setError(err.detail || 'We couldn\u2019t read that one.');
                setScreen(S_PROCESSING_ERROR);
                setStarting(false);
                return;
            }
            const data = await res.json();
            const bookmarkId = data.bookmark_id;
            // Merge fetched title / thumbnail into the placeholder so
            // ProcessingScreen can surface them above the steps.
            setBookmark(prev => ({
                ...prev,
                ...(data.title ? { title: data.title } : {}),
                ...(data.thumbnail ? { thumbnail: data.thumbnail } : {}),
                bookmark_id: bookmarkId,
                status: data.status,
            }));
            if (data.status === 'completed') {
                // Starter: 4 stages × 1.5 s = 6 s of paced checklist.
                setTimeout(() => {
                    setStarting(false);
                    navigate(`/bookmark/${bookmarkId}?anon=1&device_id=${encodeURIComponent(device_id)}`);
                }, 6300);
            } else {
                beginPolling(bookmarkId, device_id);
            }
        } catch (e) {
            try { track('anon_ingest_failed_ui', { reason: 'network' }); } catch (_) {}
            setError('Network problem. Please try again.');
            setScreen(S_PROCESSING_ERROR);
            setStarting(false);
        }
    }, [starting, navigate]);

    const fetchAndShowResult = useCallback(async (bookmarkId, deviceId) => {
        try {
            const res = await fetch(`${API}/try/bookmark/${bookmarkId}?device_id=${encodeURIComponent(deviceId)}`);
            if (res.ok) {
                const data = await res.json();
                setBookmark(data);
                setScreen(S_RESULT);
            }
        } finally {
            setStarting(false);
        }
    }, []);

    const beginPolling = useCallback((bookmarkId, deviceId) => {
        if (pollRef.current) clearInterval(pollRef.current);
        let attempts = 0;
        pollRef.current = setInterval(async () => {
            attempts += 1;
            try {
                const res = await fetch(`${API}/try/bookmark/${bookmarkId}?device_id=${encodeURIComponent(deviceId)}`);
                if (!res.ok) return;
                const data = await res.json();
                if (data.status === 'completed' || data.summary) {
                    clearInterval(pollRef.current);
                    setProcessingCompleted(true);
                    // Small delay so the "Filing it" checkmark has time
                    // to animate in before we route away.
                    setTimeout(() => {
                        setStarting(false);
                        navigate(`/bookmark/${bookmarkId}?anon=1&device_id=${encodeURIComponent(deviceId)}`);
                    }, 850);
                } else if (data.status === 'failed') {
                    clearInterval(pollRef.current);
                    setError('Processing failed. Try a different link.');
                    setScreen(S_LANDING);
                    setStarting(false);
                }
            } catch { /* transient; keep polling */ }
            if (attempts > 90) { // 3 min ceiling
                clearInterval(pollRef.current);
                setError('Taking too long. Please try a different link.');
                setScreen(S_LANDING);
                setStarting(false);
            }
        }, 2000);
    }, [navigate]);

    const handleKeepForever = useCallback(() => {
        if (bookmark?.bookmark_id) stashPendingClaim(bookmark.bookmark_id);
        setShowSignupWall(true);
    }, [bookmark]);

    const handleGoogleSignup = useCallback(() => {
        // Directly kick off Google OAuth using the shared AuthContext
        // helper. localStorage already contains the pending-claim
        // payload (stashed inside handleKeepForever), so `checkAuth`
        // will migrate the bookmark on the way back from the OAuth
        // round-trip.
        login();
    }, [login]);

    const handleEmailSignup = useCallback((mode = 'register') => {
        setEmailMode(mode);
        setShowSignupWall(false);
        setShowEmailSheet(true);
    }, []);

    const reset = useCallback(() => {
        if (pollRef.current) clearInterval(pollRef.current);
        setScreen(S_LANDING);
        setUrl('');
        setBookmark(null);
        setError('');
        setStarting(false);
    }, []);

    return (
        <div className="min-h-[100dvh] w-full max-w-md mx-auto flex flex-col" style={{ background: CANVAS, fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }} data-testid="try-it-flow">
            {whoami && (
                <div
                    className="fixed inset-x-0 top-0 z-[60] mx-auto max-w-md m-3 rounded-2xl p-4 flex items-start gap-3 shadow-xl border"
                    style={{ background: '#0F172A', color: '#fff', borderColor: 'rgba(255,255,255,.08)' }}
                    data-testid="whoami-banner"
                >
                    <div className="min-w-0 flex-1">
                        <p className="text-[12px] font-bold uppercase tracking-widest" style={{ color: '#6EE7B7' }}>Your device_id</p>
                        <p className="text-[12px] font-mono mt-1.5 break-all leading-snug" style={{ color: '#E5E7EB' }} data-testid="whoami-value">{whoami}</p>
                        <p className="text-[11px] mt-2" style={{ color: '#94A3B8' }}>Paste this into TRY_IT_BYPASS_DEVICE_IDS.</p>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                        <button
                            type="button"
                            onClick={async () => {
                                try { await navigator.clipboard.writeText(whoami); }
                                catch (_) { window.prompt('Copy your device_id:', whoami); }
                            }}
                            className="text-[11.5px] font-bold rounded-full px-3 py-1.5"
                            style={{ background: '#10B981', color: '#fff' }}
                            data-testid="whoami-copy-btn"
                        >
                            Copy
                        </button>
                        <button
                            type="button"
                            onClick={() => setWhoami(null)}
                            className="text-[11px] font-medium px-3 py-1"
                            style={{ color: '#94A3B8' }}
                            data-testid="whoami-close-btn"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}
            {screen === S_LANDING && (
                <LandingScreen
                    url={url}
                    setUrl={setUrl}
                    onSubmit={() => startProcessing(url)}
                    onStarter={(id, title) => startProcessing('', { starter: id, starterTitle: title })}
                    onSignIn={() => handleEmailSignup('login')}
                    onTryFree={() => handleEmailSignup('register')}
                    starting={starting}
                    error={error}
                />
            )}
            {screen === S_PROCESSING && (
                <ProcessingScreen
                    url={bookmark?.url || 'Starter clip'}
                    title={bookmark?.title}
                    thumbnail={bookmark?.thumbnail}
                    completed={processingCompleted}
                    mode={bookmark?.starter ? 'fake' : 'real'}
                />
            )}
            {screen === S_PROCESSING_ERROR && (
                <ProcessingErrorScreen
                    url={bookmark?.url || ''}
                    message={error}
                    onRetry={() => {
                        // Same URL / starter re-fire. `starter` gets
                        // forwarded so demo tiles don't fall back to a
                        // real ingest.
                        setError('');
                        if (bookmark?.starter) {
                            startProcessing('', { starter: bookmark.starter === true ? undefined : bookmark.starter, starterTitle: bookmark?.title });
                        } else {
                            startProcessing(bookmark?.url || '');
                        }
                    }}
                    onBack={() => { setError(''); setScreen(S_LANDING); }}
                />
            )}
            {screen === S_RESULT && bookmark && (
                <ResultScreen
                    bookmark={bookmark}
                    onKeep={handleKeepForever}
                    onReset={reset}
                />
            )}
            {showSignupWall && bookmark && (
                <SignupWall
                    bookmark={bookmark}
                    onGoogle={handleGoogleSignup}
                    onEmail={() => handleEmailSignup('register')}
                    onSignIn={() => handleEmailSignup('login')}
                    onClose={() => setShowSignupWall(false)}
                />
            )}
            {showEmailSheet && (
                <EmailAuthSheet
                    open={showEmailSheet}
                    initialMode={emailMode}
                    onOpenChange={(open) => setShowEmailSheet(open)}
                />
            )}
        </div>
    );
}

// ─────────────────────────── LANDING ───────────────────────────
function LandingScreen({ url, setUrl, onSubmit, onStarter, onSignIn, onTryFree, starting, error }) {
    return (
        <>
            <header className="flex items-center justify-between px-5 pt-4 pb-1">
                <div className="flex items-center gap-2.5">
                    <img src="/icon-192.png" alt="" className="w-8 h-8 rounded-lg" />
                    <span className="text-[19px] font-bold tracking-tight" style={{ color: INK }}>
                        Brain<span style={{ color: EMERALD }}>Sync</span>
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={onSignIn}
                        className="text-sm font-semibold px-2.5 py-3"
                        style={{ color: SECONDARY }}
                        data-testid="try-signin-btn"
                    >
                        Sign in
                    </button>
                    <button
                        onClick={onTryFree}
                        className="text-sm font-bold rounded-full px-4 h-10 inline-flex items-center active:scale-[.97] transition-transform"
                        style={{ background: EMERALD, color: '#fff' }}
                        data-testid="try-free-pill-btn"
                    >
                        Try it free
                    </button>
                </div>
            </header>

            <main className="px-5 pt-4 flex-1">
                <div className="flex items-center gap-2 mt-3 mb-2.5" data-testid="hero-eyebrow">
                    <span className="text-[13px] leading-none" style={{ color: EMERALD }}>✦</span>
                    <span className="text-[11.5px] font-mono uppercase tracking-[0.2em] font-medium" style={{ color: EMERALD }}>
                        Your second brain
                    </span>
                </div>
                <h1 className="text-[clamp(33px,9vw,40px)] leading-[1.06] font-extrabold tracking-tight mb-3.5" style={{ color: '#24423A' }} data-testid="try-hero-headline">
                    Save anything.<br />Ask it <span style={{ color: EMERALD }}>anything</span><span style={{ color: EMERALD }}>.</span>
                </h1>
                <p className="text-[15.5px] leading-[1.55] max-w-[36ch] mb-6" style={{ color: SECONDARY }} data-testid="try-hero-subcopy">
                    Every video, article and link, in <b style={{ color: INK }}>one place that remembers</b>. BrainSync transcribes it, summarises it and files it <b style={{ color: INK }}>in seconds</b> - then just ask.
                </p>

                <div
                    className="flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5 transition-all border focus-within:border-emerald-500/40 focus-within:shadow-[0_0_0_4px_rgba(16,185,129,0.12)]"
                    style={{ background: '#F0F2EE', borderColor: 'transparent' }}
                >
                    <input
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && onSubmit()}
                        placeholder="Paste any link. See what happens..."
                        autoComplete="off"
                        inputMode="url"
                        className="flex-1 min-w-0 bg-transparent border-0 outline-none focus:outline-none focus:ring-0 focus:border-0 text-[15px] font-medium py-3 placeholder:font-normal placeholder:text-[13.5px] appearance-none"
                        style={{ color: INK, WebkitTapHighlightColor: 'transparent' }}
                        data-testid="try-url-input"
                    />
                    <button
                        onClick={onSubmit}
                        disabled={starting}
                        className="rounded-full font-bold text-[15px] px-4 h-11 inline-flex items-center gap-1.5 disabled:opacity-60 shrink-0"
                        style={{ background: EMERALD, color: '#fff' }}
                        data-testid="try-save-btn"
                    >
                        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Save <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                </div>
                <p className="text-[11px] tracking-widest font-mono uppercase mt-2.5 ml-1" style={{ color: '#8A968F' }}>
                    ↑ Leave it empty to use a sample link
                </p>
                {error && (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800" data-testid="try-error">
                        {error}
                    </div>
                )}

                <div className="mt-8">
                    <h3 className="text-[17px] font-bold tracking-tight" style={{ color: '#24423A' }}>Save one video. See what happens.</h3>
                    <p className="text-[13.5px] mt-1" style={{ color: SECONDARY }}>Real videos, already transcribed. Tap one and watch it happen.</p>
                </div>
            </main>

            <div className="flex gap-3 overflow-x-auto px-5 py-3 scroll-pl-5 scroll-pr-5 snap-x snap-mandatory scrollbar-none" data-testid="starter-shelf">
                {STARTERS.map(s => (
                    <button
                        key={s.id}
                        onClick={() => onStarter(s.id, s.title)}
                        disabled={starting}
                        className="shrink-0 w-[180px] text-left rounded-2xl border p-2.5 bg-white active:scale-[.975] transition-transform snap-start disabled:opacity-60"
                        style={{ borderColor: '#E4E8E3', boxShadow: '0 2px 8px rgba(15,23,42,.05)' }}
                        data-testid={`starter-tile-${s.id}`}
                    >
                        <div className="relative h-[100px] rounded-xl overflow-hidden mb-2.5 bg-slate-200">
                            <img
                                src={`https://i.ytimg.com/vi/${s.yt}/hqdefault.jpg`}
                                alt=""
                                loading="lazy"
                                className="absolute inset-0 w-full h-full object-cover"
                                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                            <div className="absolute inset-0 grid place-items-center">
                                <span className="w-9 h-9 rounded-full grid place-items-center backdrop-blur-sm" style={{ background: 'rgba(15,23,42,.72)' }}>
                                    <Play className="w-4 h-4 text-white ml-0.5" />
                                </span>
                            </div>
                        </div>
                        <div className="text-[10px] font-medium tracking-[.18em] uppercase mb-1" style={{ color: EMERALD }}>{s.kicker}</div>
                        <h4 className="text-[13.5px] font-bold leading-[1.3] mb-1.5 line-clamp-2" style={{ color: INK }}>{s.title}</h4>
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[10.5px] font-mono uppercase tracking-wider" style={{ color: '#8A968F' }}>{s.duration}</span>
                            <span
                                className="rounded-full font-bold text-[11px] px-2.5 py-1 inline-flex items-center gap-1 pointer-events-none shrink-0"
                                style={{ background: EMERALD, color: '#fff', boxShadow: '0 1px 6px rgba(15,23,42,.18)' }}
                                data-testid={`starter-save-pill-${s.id}`}
                            >
                                Save <ArrowRight className="w-3 h-3" />
                            </span>
                        </div>
                    </button>
                ))}
            </div>

            <div className="px-5 pt-5 pb-3">
                <div
                    className="rounded-[20px] border overflow-hidden"
                    style={{ background: '#FFFFFF', borderColor: '#E4E8E3', boxShadow: '0 2px 8px rgba(15,23,42,.05)' }}
                    data-testid="capabilities-list"
                >
                    <CapRow
                        tileBg="#F9DCC4"
                        icon={<Command className="w-[19px] h-[19px]" style={{ color: '#0F172A' }} />}
                        title="One place for everything"
                        body={<>YouTube, TikTok, articles, podcasts, PDFs - every save from every app, together in one brain.</>}
                    />
                    <CapRow
                        tileBg="#DDD3F5"
                        icon={<AlignLeft className="w-[19px] h-[19px]" style={{ color: '#0F172A' }} />}
                        title="It actually remembers"
                        body={<>Videos get transcribed, articles get summarised, everything gets filed - automatically.</>}
                    />
                    <CapRow
                        tileBg="#CDEBD9"
                        icon={<ArrowRight className="w-[19px] h-[19px]" style={{ color: '#0F172A' }} />}
                        title="It knows what you sent"
                        body={<>A recipe video becomes <b style={{ color: INK }}>a recipe</b>. A trailer becomes <b style={{ color: INK }}>a movie page</b>. A song becomes <b style={{ color: INK }}>its lyrics and story</b>.</>}
                    />
                    <CapRow
                        tileBg="#F9DCC4"
                        icon={<span className="text-[19px] leading-none" style={{ color: '#0F172A' }}>✦</span>}
                        title="Then ask it anything"
                        body={<>“What was that video about pricing?” Your library answers back - with the source.</>}
                        last
                    />
                </div>
            </div>

            <footer className="mt-auto text-center px-5 pt-3 pb-[calc(24px+env(safe-area-inset-bottom))]">
                <p className="text-[13.5px]" style={{ color: SECONDARY }}>
                    Already have an account?{' '}
                    <button onClick={onSignIn} className="font-bold" style={{ color: EMERALD }} data-testid="try-signin-footer">Sign in</button>
                </p>
            </footer>
        </>
    );
}

function CapRow({ tileBg, icon, title, body, last }) {
    return (
        <div
            className="flex items-start gap-4 px-[18px] py-[18px]"
            style={last ? undefined : { borderBottom: '1px solid #EDF0EC' }}
            data-testid={`cap-row-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
            <div className="w-[42px] h-[42px] rounded-xl flex items-center justify-center shrink-0" style={{ background: tileBg }}>
                {icon}
            </div>
            <div className="min-w-0">
                <div className="text-[16.5px] font-bold tracking-tight" style={{ color: '#24423A', letterSpacing: '-0.01em' }}>{title}</div>
                <div className="text-[14px] leading-[1.5] mt-1" style={{ color: SECONDARY }}>{body}</div>
            </div>
        </div>
    );
}

function Prop({ icon, bg, title, sub }) {
    return (
        <div className="flex items-center gap-3 py-3">
            <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: bg }}>{icon}</span>
            <div>
                <b className="block text-[14.5px] font-bold" style={{ color: INK }}>{title}</b>
                <span className="text-[13px]" style={{ color: SECONDARY }}>{sub}</span>
            </div>
        </div>
    );
}

// ─────────────────────────── PROCESSING ERROR ───────────────────────────
// Inline error card rendered when the ingest fails mid-flight
// (network, invalid URL, backend 4xx/5xx other than 429 — 429 opens
// the sign-up sheet instead). Never bounces silently back to the
// landing; the user always sees WHY it failed and gets an explicit
// retry / back affordance. (Fix A step 2.)
function ProcessingErrorScreen({ url, message, onRetry, onBack }) {
    return (
        <div className="flex-1 flex flex-col justify-center px-6" data-testid="try-processing-error">
            <div className="w-[76px] h-[76px] rounded-3xl grid place-items-center mb-6 shrink-0" style={{ background: '#0F172A' }}>
                <XIcon className="w-8 h-8" style={{ color: '#F87171' }} />
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight mb-2" style={{ color: '#24423A' }}>Couldn&apos;t read that one.</h2>
            {url && (
                <p className="font-mono text-[11.5px] break-all mb-4" style={{ color: '#8A968F' }}>{url}</p>
            )}
            {message && (
                <p className="text-[13.5px] mb-6" style={{ color: SECONDARY }}>{message}</p>
            )}
            <div className="flex flex-col gap-3">
                <button
                    onClick={onRetry}
                    className="w-full rounded-full font-bold text-[15.5px] min-h-[52px]"
                    style={{ background: EMERALD, color: '#fff' }}
                    data-testid="try-error-retry"
                >
                    Try again
                </button>
                <button
                    onClick={onBack}
                    className="w-full rounded-full font-bold text-[15.5px] min-h-[52px] border"
                    style={{ background: '#fff', color: INK, borderColor: '#DDE2DC' }}
                    data-testid="try-error-back"
                >
                    Back
                </button>
            </div>
        </div>
    );
}

// ─────────────────────────── PROCESSING ───────────────────────────
// Two pacing modes:
// - "real"  → stages weighted by actual pipeline cost (transcribe is
//             the slow one). Last stage waits for `completed` before
//             ticking so we never race ahead of the backend.
// - "fake"  → equal-time stages for pre-processed starters. Each
//             stage takes the same ~1.5 s so the user has time to
//             read every label. Ignores `completed`.
// Set via the `mode` prop from the parent.
function ProcessingScreen({ url, title, thumbnail, completed, mode = 'real' }) {
    const STEPS = ['Reading it', 'Transcribing', 'Understanding', 'Filing it'];
    const FAKE_STAGE_MS = 1500;  // 4 × 1.5 s = 6 s total
    const REAL_STAGE_DEADLINES = [2000, 26000, 42000, 999_999];
    const [current, setCurrent] = useState(0);
    const startedAt = useRef(Date.now());

    useEffect(() => {
        const tick = setInterval(() => {
            const elapsed = Date.now() - startedAt.current;
            setCurrent(prev => {
                if (mode === 'fake') {
                    // Advance one stage per FAKE_STAGE_MS. Cap at last
                    // stage; the parent decides when to route away.
                    const target = Math.min(STEPS.length - 1, Math.floor(elapsed / FAKE_STAGE_MS));
                    return target > prev ? target : prev;
                }
                let next = prev;
                while (next < STEPS.length - 1 && elapsed >= REAL_STAGE_DEADLINES[next]) next += 1;
                return next;
            });
        }, 250);
        return () => clearInterval(tick);
    }, [mode]);

    // Race-forward on real-mode completion (see comment above).
    useEffect(() => {
        if (mode === 'fake' || !completed) return;
        let stage = current;
        const roll = setInterval(() => {
            stage += 1;
            setCurrent(stage);
            if (stage >= STEPS.length) clearInterval(roll);
        }, 260);
        return () => clearInterval(roll);
    }, [completed, mode]);

    // Deterministic progress-bar width.
    const barPercent = (() => {
        if (mode === 'fake') return Math.min(100, ((current + 1) / STEPS.length) * 100);
        if (completed) return 100;
        const base = Math.min(90, (current / STEPS.length) * 90 + 5);
        return base;
    })();

    return (
        <div className="flex-1 flex flex-col justify-center px-6" data-testid="try-processing">
            <div className="w-[76px] h-[76px] rounded-3xl grid place-items-center mb-7 shrink-0" style={{ background: '#0F172A', boxShadow: '0 12px 32px rgba(15,23,42,.18)' }}>
                <img src="/icon-192.png" alt="" className="w-[76px] h-[76px] rounded-3xl animate-pulse" />
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight mb-2" style={{ color: '#24423A' }}>{title || 'Reading it now'}</h2>
            <p className="font-mono text-[11.5px] break-all mb-4" style={{ color: '#8A968F' }}>{url}</p>
            {thumbnail && (
                <div className="mb-4 rounded-xl overflow-hidden bg-slate-100" style={{ maxWidth: 260 }}>
                    <img src={thumbnail} alt="" className="w-full h-auto block" loading="lazy" />
                </div>
            )}
            <div>
                {STEPS.map((label, i) => {
                    const isDone = i < current;
                    const isNow = i === current;
                    return (
                        <div key={label} className="flex items-center gap-3 py-2.5">
                            <span className="font-mono text-[11px] font-bold tracking-wider w-5 shrink-0" style={{ color: (isNow || isDone) ? EMERALD : '#8A968F' }}>{String(i + 1).padStart(2, '0')}</span>
                            <span className="text-[15px] font-semibold flex-1" style={{ color: isDone ? INK : (isNow ? '#24423A' : '#8A968F') }}>{label}</span>
                            <span className="w-5 h-5 rounded-full grid place-items-center shrink-0" style={{
                                background: isDone ? EMERALD : 'transparent',
                                border: `1.5px solid ${(isNow || isDone) ? EMERALD : '#DDE2DC'}`,
                                borderTopColor: isNow ? 'transparent' : (isDone ? EMERALD : '#DDE2DC'),
                                animation: isNow ? 'spin .8s linear infinite' : 'none',
                            }}>
                                {isDone && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                            </span>
                        </div>
                    );
                })}
            </div>
            <div className="h-[3px] mt-7 rounded-full overflow-hidden" style={{ background: '#EDF1EC' }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ background: EMERALD, width: `${barPercent}%` }} />
            </div>
            {current >= 1 && !completed && (
                <p className="text-[11.5px] text-center mt-4 font-mono uppercase tracking-wider" style={{ color: '#8A968F' }}>
                    Longer videos can take up to a minute
                </p>
            )}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

// ─────────────────────────── RESULT ───────────────────────────
function ResultScreen({ bookmark, onKeep, onReset }) {
    const summary = bookmark.summary || bookmark.description || 'Summary is ready - sign up to see the full breakdown.';
    // Points: extract bullets from summary if present ("- x" lines), else split sentences.
    const pointsFromSummary = (() => {
        const s = bookmark.summary || '';
        const bullets = s.split('\n').map(l => l.replace(/^[-•*]\s*/, '').trim()).filter(l => l.length > 12 && l.length < 220);
        if (bullets.length >= 3) return bullets.slice(0, 5);
        return s.split(/[.!?]\s+/).filter(x => x.length > 24).slice(0, 3);
    })();
    const tags = (bookmark.tags && bookmark.tags.length ? bookmark.tags : [bookmark.content_type || 'article']).slice(0, 6);

    return (
        <div className="flex-1 flex flex-col" data-testid="try-result">
            <header className="flex items-center justify-between px-5 pt-4 min-h-14">
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium font-mono tracking-widest uppercase" style={{ background: '#E3F1E9', color: '#1F7A55' }}>
                    <Sparkles className="w-3 h-3" /> Ready
                </span>
                <button onClick={onReset} className="text-sm font-semibold px-1 py-3" style={{ color: SECONDARY }} data-testid="try-start-over">Start over</button>
            </header>

            <div className="mx-5 mt-3 bg-white rounded-2xl border overflow-hidden" style={{ borderColor: '#E4E8E3', boxShadow: '0 2px 8px rgba(15,23,42,.05)' }}>
                {bookmark.thumbnail ? (
                    <img src={bookmark.thumbnail} alt="" className="w-full h-[152px] object-cover" />
                ) : (
                    <div className="h-[152px] grid place-items-center" style={{ background: '#CBB8F0' }}>
                        <span className="w-12 h-12 rounded-full grid place-items-center" style={{ background: '#0F172A' }}>
                            <Play className="w-5 h-5 text-white ml-0.5" />
                        </span>
                    </div>
                )}
                <div className="p-4">
                    <div className="text-[10px] font-medium tracking-[.2em] uppercase mb-1.5" style={{ color: EMERALD }}>
                        {bookmark.site_name || bookmark.content_type || 'article'}
                    </div>
                    <h2 className="text-[21px] leading-tight font-extrabold tracking-tight mb-2.5" style={{ color: '#24423A' }}>{bookmark.title || 'Untitled'}</h2>
                    <p className="text-[14.5px] leading-[1.55] mb-4" style={{ color: SECONDARY }}>{summary.slice(0, 340)}</p>
                    {pointsFromSummary.length > 0 && (
                        <>
                            <p className="text-[10px] font-medium tracking-widest uppercase font-mono mb-2" style={{ color: '#8A968F' }}>Key points</p>
                            <ul className="flex flex-col gap-2 mb-4">
                                {pointsFromSummary.map((p, i) => (
                                    <li key={i} className="flex gap-2.5 text-[14px] leading-snug" style={{ color: INK }}>
                                        <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: EMERALD }} />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {tags.length > 0 && (
                        <>
                            <p className="text-[10px] font-medium tracking-widest uppercase font-mono mb-2" style={{ color: '#8A968F' }}>Auto-tagged</p>
                            <div className="flex flex-wrap gap-1.5">
                                {tags.map((t, i) => (
                                    <span key={i} className="font-mono text-[11.5px] rounded-full px-2.5 py-1.5" style={{ background: '#F0F2EE', color: SECONDARY }}>{t}</span>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="mx-5 mt-3.5 flex items-center gap-2.5 rounded-full border border-dashed px-4 min-h-13 cursor-pointer" style={{ borderColor: '#DDE2DC' }} onClick={onKeep}>
                <SearchIcon className="w-4 h-4" style={{ color: EMERALD }} />
                <span className="text-[14.5px]" style={{ color: '#8A968F' }}>Ask this bookmark anything</span>
            </div>

            <div className="sticky bottom-0 mt-auto px-5 pt-4 pb-[calc(18px+env(safe-area-inset-bottom))]" style={{ background: `linear-gradient(to bottom, rgba(253,252,248,0), ${CANVAS} 28%)` }}>
                <button
                    onClick={onKeep}
                    className="w-full rounded-full font-bold text-[16px] min-h-[54px]"
                    style={{ background: EMERALD, color: '#fff' }}
                    data-testid="try-keep-forever"
                >
                    Keep this forever
                </button>
                <p className="text-[10.5px] font-mono uppercase tracking-wider text-center mt-3" style={{ color: '#8A968F' }}>Held on this device only</p>
            </div>
        </div>
    );
}

// ─────────────────────────── SIGN-UP WALL ───────────────────────────
function SignupWall({ bookmark, onGoogle, onEmail, onSignIn, onClose }) {
    return (
        <div className="fixed inset-0 z-50" data-testid="try-signup-wall">
            <div className="absolute inset-0 bg-black/45" onClick={onClose} />
            <div className="absolute left-0 right-0 bottom-0 rounded-t-3xl px-5 pb-[calc(22px+env(safe-area-inset-bottom))] pt-2.5 animate-in slide-in-from-bottom duration-300" style={{ background: CANVAS, boxShadow: '0 -14px 44px rgba(15,23,42,.2)' }}>
                <div className="w-10 h-1 rounded-full mx-auto mb-4" style={{ background: '#E4E8E3' }} />
                <div className="flex items-center gap-3 rounded-2xl border p-2.5 mb-5" style={{ borderColor: '#E4E8E3', background: '#fff', boxShadow: '0 2px 8px rgba(15,23,42,.05)' }}>
                    <div className="w-[54px] h-[54px] rounded-xl grid place-items-center shrink-0" style={{ background: '#CBB8F0' }}>
                        {bookmark.thumbnail ? (
                            <img src={bookmark.thumbnail} alt="" className="w-full h-full object-cover rounded-xl" />
                        ) : (
                            <Play className="w-4 h-4 text-white" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <h5 className="text-[13.5px] font-bold mb-1 truncate" style={{ color: INK }}>{bookmark.title || 'Your bookmark'}</h5>
                        <p className="text-[10.5px] font-mono uppercase tracking-wider" style={{ color: EMERALD }}>1 bookmark waiting to sync</p>
                    </div>
                </div>
                <h2 className="text-[25px] leading-tight font-extrabold tracking-tight mb-2" style={{ color: '#24423A' }}>Keep it forever?</h2>
                <p className="text-[14.5px] leading-[1.5] mb-5" style={{ color: SECONDARY }}>Create a free account and this bookmark follows you to every device. Takes one tap.</p>
                <button
                    onClick={onGoogle}
                    className="w-full rounded-full font-bold text-[15.5px] min-h-[54px] flex items-center justify-center gap-3 border transition-transform active:scale-[.98]"
                    style={{ background: '#fff', color: '#3C4043', borderColor: '#DADCE0', boxShadow: '0 1px 2px rgba(0,0,0,.03)', fontFamily: "'Google Sans', 'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                    data-testid="wall-google-btn"
                >
                    <GoogleG large />
                    Continue with Google
                </button>
                <div className="flex items-center justify-center gap-3 mt-3.5 text-[13px]" style={{ color: '#8A968F' }}>
                    <button onClick={onEmail} className="font-bold px-1 min-h-11" style={{ color: SECONDARY }} data-testid="wall-email-btn">Sign up with email</button>
                    <span>·</span>
                    <button onClick={onSignIn} className="font-bold px-1 min-h-11" style={{ color: SECONDARY }} data-testid="wall-signin-btn">I have an account</button>
                </div>
                <p className="text-[10px] tracking-widest font-mono uppercase text-center mt-4" style={{ color: '#8A968F' }}>Free forever for basics · No credit card</p>
                <button onClick={onClose} className="block w-full text-[13.5px] py-3.5 mt-1" style={{ color: '#8A968F' }} data-testid="wall-later-btn">Not yet, let me look around</button>
            </div>
        </div>
    );
}

function GoogleG({ large }) {
    const size = large ? 22 : 14;
    return (
        <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
            <path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h5.9c-.3 1.4-1 2.6-2.2 3.4v2.8h3.6c2.1-2 3.2-4.9 3.2-8.3z" />
            <path fill="#34A853" d="M12 23c2.9 0 5.4-1 7.2-2.6l-3.6-2.8c-1 .7-2.3 1.1-3.6 1.1-2.8 0-5.1-1.9-6-4.4H2.3v2.8C4 20.6 7.7 23 12 23z" />
            <path fill="#FBBC04" d="M6 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.9H2.3C1.5 8.5 1 10.2 1 12s.5 3.5 1.3 5.1L6 14.3z" />
            <path fill="#EA4335" d="M12 5.4c1.6 0 3 .6 4.1 1.6l3.1-3.1C17.4 2.1 14.9 1 12 1 7.7 1 4 3.4 2.3 6.9L6 9.7c.9-2.5 3.2-4.3 6-4.3z" />
        </svg>
    );
}
